/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, X, Mail, Radar, Sparkles, Link2,
  ArrowRight, Loader2, Heart, Home, MessageSquare, MousePointerClick,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";

// Public score bands MUST stay aligned with backend monitoring.py.
const BAND_COPY = {
  green: { label: "Healthy", pill: "bg-emerald-50 text-emerald-700 border-emerald-100", ring: "border-emerald-100 bg-emerald-50", num: "text-charcoal-900", dot: "bg-emerald-500" },
  amber: { label: "Needs attention", pill: "bg-amber-50 text-amber-800 border-amber-200", ring: "border-amber-200 bg-amber-50", num: "text-charcoal-900", dot: "bg-amber-500" },
  red:   { label: "Action required", pill: "bg-red-50 text-red-800 border-red-200", ring: "border-red-200 bg-red-50", num: "text-charcoal-900", dot: "bg-red-500" },
};

const CAT_DOT = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500", info: "bg-charcoal-300" };

export default function SampleReport({ open, onClose }) {
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (open && !report) {
      api.get("/sample-report").then(({ data }) => setReport(data)).catch(() => {});
    }
  }, [open, report]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const status = report?.overall_status || "green";
  const band = BAND_COPY[status] || BAND_COPY.green;

  return (
    <div className="fixed inset-0 z-[999] flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      data-testid="sample-report-modal">
      <div className="fixed inset-0 bg-charcoal-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl my-4 reveal in">
        <button onClick={onClose}
          className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-white shadow-soft border border-black/5 flex items-center justify-center text-charcoal-500 hover:text-charcoal-900 transition-colors"
          data-testid="sample-report-close" aria-label="Close">
          <X className="w-4 h-4" />
        </button>

        <div className="rounded-2xl bg-white shadow-premium overflow-hidden border border-black/5">
          {/* Try-it-with-your-own-site lead-capture — the very first thing prospects see. */}
          <FreeScanCTA />

          {/* Header — mimic an email preview */}
          <div className="flex items-center gap-2 px-5 py-3 border-b border-black/5 bg-charcoal-50/60">
            <Mail className="w-4 h-4 text-charcoal-400" />
            <span className="text-xs font-mono text-charcoal-500">from Mission Watch &lt;reports@missionwatch.app&gt;</span>
            <span className="ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-white border border-black/10 text-charcoal-500">Sample</span>
          </div>

          {/* 1. Mission Watch Score — no trend graph. First-time visitors have
                no context for a 6-month trend; leading with the current score
                keeps the primary message clean. */}
          <div className="px-6 sm:px-8 pt-8 pb-6 border-b border-black/5">
            <div className="flex flex-wrap items-start gap-6">
              <div className={`relative w-32 h-32 rounded-full grid place-items-center border-4 ${band.ring}`}>
                <div className="text-center">
                  <div className={`text-4xl font-semibold tracking-tighter ${band.num}`}>{report?.overall_score ?? "—"}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500 mt-0.5">Score</div>
                </div>
              </div>
              <div className="flex-1 min-w-[220px]">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border ${band.pill}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${band.dot} dot-pulse`} />
                  {report?.band_label || band.label}
                </span>
                <h3 className="text-2xl font-semibold tracking-tight text-charcoal-900 mt-2">Mission Watch Score</h3>
                <p className="text-sm text-charcoal-500 mt-1">
                  {report ? `${report.organization_name} · ${report.period}` : "Loading…"}
                </p>
              </div>
            </div>
          </div>

          {/* 2. Protection section (dark) — Mission Watch's core differentiator.
                Leads with WHAT is being monitored continuously, not just what
                a one-shot audit found. This is what makes the product
                different from every "run a scan once" tool. */}
          <ProtectionCard report={report} />

          {/* 3. Action Plan preview — a concise "what should we fix first?" */}
          <ActionPlanPreview report={report} />

          {/* 4. Website Audit preview — condensed check list. */}
          <WebsiteAuditPreview report={report} />

          {/* 5. Category Scores — closes the report with a scorecard view. */}
          {Array.isArray(report?.categories) && report.categories.length > 0 && (
            <div className="px-6 sm:px-8 py-5 border-b border-black/5" data-testid="sample-categories">
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400 mb-3">Category scores</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {report.categories.map((c) => (
                  <div key={c.name} className="flex items-center gap-3 rounded-xl border border-black/5 bg-charcoal-50/60 px-3.5 py-2.5">
                    <span className={`w-2 h-2 rounded-full ${CAT_DOT[c.status] || CAT_DOT.info}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-charcoal-800 leading-tight truncate">{c.name}</p>
                      <p className="text-[11px] font-mono text-charcoal-400 mt-0.5">{c.passing}/{c.total} passing</p>
                    </div>
                    <span className="text-sm font-semibold tabular-nums text-charcoal-900">{c.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer — reinforces continuous protection. */}
          <div className="px-6 sm:px-8 py-5 bg-charcoal-900 text-center">
            <p className="text-sm text-white/90">We'll check everything again in the next hour.</p>
            <p className="text-[11px] font-mono text-white/40 mt-1">Mission Watch · Peace of mind for small nonprofits</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Zero-commitment lead capture: prospect enters URL + email, we scan their real site
    and email them a real report in ~60-90 seconds. Sits at the top of the sample modal. */
function FreeScanCTA() {
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!website.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/free-scan", {
        website: website.trim(),
        email: email.trim(),
        organization_name: orgName.trim() || null,
      });
      setSent(true);
      toast.success(data.message || "We're on it — check your inbox in about a minute.", { duration: 8000 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-charcoal-900 text-white px-6 sm:px-8 py-6" data-testid="free-scan-sent">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/25 grid place-items-center shrink-0">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Scan started</p>
            <p className="mt-1 text-sm text-white/90 leading-relaxed">
              We're scanning <span className="text-emerald-300 break-all">{website}</span> now.
              Your report will land at <span className="text-white font-medium">{email}</span> in
              about 60–90 seconds. Check your spam folder if you don't see it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-charcoal-900 text-white px-6 sm:px-8 py-6 relative overflow-hidden" data-testid="free-scan-cta">
      <div className="absolute -top-12 -right-12 w-52 h-52 rounded-full bg-brand/20 blur-3xl pointer-events-none" />
      <div className="relative flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-md">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/25 mb-2.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 dot-pulse" />
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Free — no account needed</p>
          </div>
          <h3 className="text-lg sm:text-xl font-semibold tracking-tight leading-tight">
            Curious about your own site?
          </h3>
          <p className="mt-1.5 text-sm text-white/60 leading-relaxed">
            Enter your website below — we'll run a real Mission Watch check and email you the
            report in about a minute.
          </p>
        </div>
        <form onSubmit={submit} className="flex-1 min-w-[280px] max-w-md space-y-2" data-testid="free-scan-form">
          <input
            type="text" inputMode="url" required autoComplete="url"
            value={website} onChange={(e) => setWebsite(e.target.value)}
            placeholder="yournonprofit.org"
            className="w-full h-11 px-4 rounded-xl bg-white/[0.06] border border-white/10 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-emerald-400/40"
            data-testid="free-scan-website"
          />
          <input
            type="email" required autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yournonprofit.org"
            className="w-full h-11 px-4 rounded-xl bg-white/[0.06] border border-white/10 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-emerald-400/40"
            data-testid="free-scan-email"
          />
          <button type="submit" disabled={submitting}
            className="w-full h-11 rounded-xl bg-white text-charcoal-900 text-sm font-semibold hover:bg-emerald-50 transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="free-scan-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</> : <>Email me my free report <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-[10px] font-mono text-white/40 text-center">We'll only email you the report. No spam.</p>
        </form>
      </div>
    </div>
  );
}


/** Iter-49 — the "Protection" hero. Dark card, emerald accents, four
    monitored items rendered as icons + short truthful copy. This is
    Mission Watch's core differentiator: continuous protection, not a
    one-shot audit. Data-driven from the same report payload the
    customer dashboard reads, so what the marketing sample shows is
    exactly what a real customer will receive. */
function ProtectionCard({ report }) {
  const cov = report?.donation_coverage;
  const checks = report?.checks || [];
  const findCheck = (k) => checks.find((c) => c.key === k);
  const contactCheck  = findCheck("contact");
  const websiteCheck  = findCheck("website");
  const mobileFlow    = report?.mobile_flow_preview;

  // Derive item state directly from the same checks the customer
  // dashboard renders — sample and real experience never diverge.
  const items = [
    {
      icon: Radar,
      label: "Donation links",
      copy: cov
        ? `${cov.verified} of ${cov.total} watched — checked ${cov.last_checked_min_ago} min ago`
        : "Every donation button and form monitored continuously",
      ok: !cov || cov.verified === cov.total,
    },
    {
      icon: MessageSquare,
      label: "Contact forms",
      copy: contactCheck
        ? contactCheck.detail
        : "Contact page and form detected",
      ok: !contactCheck || contactCheck.status === "green",
    },
    {
      icon: Home,
      label: "Homepage availability",
      copy: websiteCheck
        ? websiteCheck.detail
        : "Homepage reachability verified every hour",
      ok: !websiteCheck || websiteCheck.status === "green",
    },
    {
      icon: MousePointerClick,
      label: "Mobile donation flow",
      copy: mobileFlow?.enabled
        ? (mobileFlow.passed
            ? "Donate button tapped on real mobile Chrome — works"
            : "Donate button tested on mobile — issue detected")
        : "We tap the Donate button on a real phone every day",
      ok: !mobileFlow?.enabled || mobileFlow.passed,
    },
  ];

  return (
    <div className="mx-6 sm:mx-8 mt-6 mb-2 rounded-2xl bg-charcoal-900 text-white p-6 sm:p-7 relative overflow-hidden" data-testid="sample-protection">
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/25">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 dot-pulse" />
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Actively monitoring</p>
          </span>
          <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60">Continuous protection</span>
        </div>
        <h3 className="text-lg sm:text-xl font-semibold text-white leading-snug">
          The pages your donors touch — watched 24/7.
        </h3>
        <p className="mt-1.5 text-[13px] text-white/60 leading-relaxed max-w-lg">
          Mission Watch checks the critical parts of your site every hour,
          then tells you the moment something breaks. Not a one-time audit —
          a quiet, always-on protection layer.
        </p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {items.map((it, i) => (
            <div key={i}
                 data-testid={`protection-item-${it.label.toLowerCase().replace(/\s+/g,'-')}`}
                 className="flex items-start gap-3 rounded-xl bg-white/[0.04] border border-white/10 px-3.5 py-3">
              <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 border ${
                it.ok
                  ? "bg-emerald-500/15 border-emerald-500/25"
                  : "bg-amber-500/15 border-amber-500/25"
              }`}>
                <it.icon className={`w-4 h-4 ${it.ok ? "text-emerald-400" : "text-amber-400"}`} strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-white leading-tight">{it.label}</p>
                <p className="text-[12px] text-white/60 mt-0.5 leading-snug">{it.copy}</p>
              </div>
              {it.ok && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-1 shrink-0" strokeWidth={2} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/** Iter-49 — Action Plan preview. Blends AI summary + new-this-month
    into one "here's what to focus on" strip. Truthful and short —
    the real customer report goes deeper on the dashboard. */
function ActionPlanPreview({ report }) {
  if (!report) return null;
  const items = [];
  (report.new_this_month || []).forEach((n) => {
    items.push({ tone: n.tone, text: n.text });
  });
  // Top red / amber check inline so a first-time visitor sees a
  // concrete action, not just abstract copy.
  const topIssue = (report.checks || []).find(
    (c) => c.status === "red" || c.status === "amber");
  if (topIssue && items.length < 3) {
    items.push({
      tone: topIssue.status === "red" ? "amber" : "amber",
      text: `${topIssue.label} — ${topIssue.detail}`,
    });
  }
  if (!report.ai_summary && items.length === 0) return null;

  return (
    <div className="px-6 sm:px-8 py-6 border-b border-black/5" data-testid="sample-action-plan">
      <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400 mb-3">Action plan</p>
      {report.ai_summary && (
        <p className="text-sm text-charcoal-700 leading-relaxed mb-3">{report.ai_summary}</p>
      )}
      {items.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-2">
          {items.slice(0, 4).map((n, i) => (
            <div key={i}
                 className={`flex items-start gap-2.5 px-3.5 py-3 rounded-xl border ${
                   n.tone === "amber" ? "border-amber-200 bg-amber-50" : "border-emerald-100 bg-emerald-50"
                 }`}>
              {n.tone === "amber"
                ? <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" strokeWidth={2} />
                : <Sparkles className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={2} />}
              <p className={`text-sm leading-relaxed ${n.tone === "amber" ? "text-amber-900" : "text-emerald-900"}`}>{n.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/** Iter-49 — Website Audit preview. Condensed check list — the sample
    only needs to REPRESENT the real experience, not reproduce it in
    full. Shows the first 6 checks so visitors get a sense of what a
    real report covers without the modal running past three screens. */
function WebsiteAuditPreview({ report }) {
  const checks = (report?.checks || []).slice(0, 6);
  if (checks.length === 0) return null;
  return (
    <div className="px-6 sm:px-8 py-6 border-b border-black/5" data-testid="sample-audit-preview">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Website audit preview</p>
        {(report?.checks || []).length > 6 && (
          <span className="text-[10px] font-mono text-charcoal-400">
            + {(report?.checks || []).length - 6} more in your real report
          </span>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {checks.map((c) => (
          <div key={c.key} className="flex items-start gap-2.5 py-2.5 px-3.5 rounded-lg bg-charcoal-50/60 border border-black/5">
            {c.status === "green"
              ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={2} />
              : c.status === "red"
                ? <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" strokeWidth={2} />
                : <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" strokeWidth={2} />}
            <div className="min-w-0 flex-1">
              <p className="text-sm text-charcoal-800 leading-tight">{c.label}</p>
              <p className="text-[11px] font-mono text-charcoal-400 mt-0.5 leading-snug">{c.detail}</p>
              {Array.isArray(c.items) && c.items.length > 0 && (
                <ul className="mt-2 space-y-1.5" data-testid={`sample-items-${c.key}`}>
                  {c.items.slice(0, 2).map((it, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] font-mono text-amber-800">
                      <Link2 className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                      <span className="break-all"><span className="text-charcoal-500">{it.reason}</span> · {it.url}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
